import { types as api } from '@mesa-labs/mesa-api';
import { RedactedVendorApi } from '@mesa-labs/mesa-api/dist/apis/redactedVendor';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { Partners, PatchVendorAccountTargetRequest, ProgramCodes, UUID, VendorAccountTargetRequest } from '@mesa-labs/mesa-api/dist/types';
import { prepareHeaders, useApi, useEmailUnsubscribeApi, useVendorApi } from './api';

export const LabelToDocumentType: Record<string, api.VendorDocumentType> = {
  'Bank Statement': api.VendorBankVerificationDocumentType.BANK_STATEMENT,
  'Bank Letter': api.VendorBankVerificationDocumentType.BANK_LETTER,
  'Voided Check': api.VendorBankVerificationDocumentType.VOIDED_CHECK,
  'Voided Deposit Slip': api.VendorBankVerificationDocumentType.VOIDED_DEPOSIT_SLIP,
  'Form W-9': api.VendorBusinessVerificationDocumentType.W9,
};

export const DocumentTypeToLabel: Partial<Record<api.VendorDocumentType, string>> = {
  [api.VendorBankVerificationDocumentType.BANK_STATEMENT]: 'Bank Statement',
  [api.VendorBankVerificationDocumentType.BANK_LETTER]: 'Bank Letter',
  [api.VendorBankVerificationDocumentType.VOIDED_CHECK]: 'Voided Check',
  [api.VendorBankVerificationDocumentType.VOIDED_DEPOSIT_SLIP]: 'Voided Deposit Slip',
  [api.VendorBusinessVerificationDocumentType.W9]: 'Form W-9',
};

export const vendorApi = createApi({
  reducerPath: 'vendorApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CONFIG.api.vendorUrl,
    prepareHeaders,
  }),
  tagTypes: ['Vendor', 'VendorAccounts', 'UserAccounts', 'VendorPreference', 'VendorServiceAgreements', 'PartnerVendorServiceAgreements', 'VendorDocuments', 'Programs', 'ProgramPromotions', 'VendorProgramPortalConfiguration'],
  endpoints: (build) => ({
    validateOnboardingId:
      build.query<api.RedactedOnboardingResponse | undefined, { onboardingId: string }>({
        async queryFn({ onboardingId }, { dispatch }) {
          const unauthenticatedExternalVendorApi = useApi(CONFIG.api.vendorUrl, RedactedVendorApi, dispatch, false);
          const data = await unauthenticatedExternalVendorApi.getOnboarding<api.RedactedOnboardingResponse>(onboardingId);
          return { data };
        },
      }),
    registerOnboarding:
      build.query<api.RedactedOnboardingResponse | undefined, { onboardingId: string }>({
        async queryFn({ onboardingId }, { dispatch }) {
          const externalVendorApi = useVendorApi(dispatch);
          const data = await externalVendorApi.registerOnboarding<api.RedactedOnboardingResponse>(onboardingId);
          return { data };
        },
      }),
    getVendor: build.query<api.RedactedVendorAggregateResponse | undefined, { id: string }>({
      async queryFn({ id }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.getVendor<api.RedactedVendorAggregateResponse>(id);
        return { data };
      },
      providesTags: (result, error, id) => [{ type: 'Vendor', ...id, ...result }],
    }),
    createUnassignedVendor: build.mutation<api.RedactedVendorAggregateResponse, api.UnassignedVendorRequest>({
      async queryFn(arg, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.createVendor<api.RedactedVendorAggregateResponse>(arg);
        return { data };
      },
    }),
    createAssignedVendor: build.mutation<api.RedactedVendorAggregateResponse, api.VendorRequest & { partnerId: number; externalVendorId: string }>({
      async queryFn({ partnerId, externalVendorId, ...body }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.createVendorByExternalIdentifiers<api.RedactedVendorAggregateResponse>(
          partnerId,
          externalVendorId,
          body,
        );
        return { data };
      },
    }),
    updateVendor: build.mutation<api.RedactedVendorResponse | undefined, api.UpdateVendorRequest & { id: string }>({
      async queryFn({ id, ...body }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.updateVendor<api.RedactedVendorResponse>(id, body);
        return { data };
      },
      invalidatesTags: ['Vendor'],
    }),
    requestInflights: build.mutation<api.RedactedVendorResponse | undefined, api.VendorInFlightsRequest & { id: string }>({
      async queryFn({ id, ...body }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.requestInFlights<api.RedactedVendorResponse>(id, body);
        return { data };
      },
      invalidatesTags: ['Vendor'],
    }),
    confirmVerification:
      build.mutation<api.RedactedVendorAccountResponse | undefined, api.VendorAccountVerificationRequest & { vendorId: string; vendorAccountId: string; }>({
        async queryFn({ vendorId, vendorAccountId, ...body }, { dispatch }) {
          const externalVendorApi = useVendorApi(dispatch);
          const data = await externalVendorApi.verifyVendorAccount<api.RedactedVendorAccountAggregateResponse>(vendorId, vendorAccountId, body);
          return { data };
        },
        // optimistically update the getVendor query so that the primary
        // account is considered 'succeeded' until page refresh re-triggers
        // the getVendor query
        async onQueryStarted({ vendorId, vendorAccountId }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            vendorApi.util.updateQueryData('getVendor', { id: vendorId }, (draft) => {
              const updatedAccounts = draft?.accounts?.map((account) => {
                if (account.id === vendorAccountId) {
                  return {
                    ...account,
                    verificationStatus: 'succeeded',
                  };
                }

                return account;
              });

              Object.assign(draft || {}, {
                ...draft,
                accounts: updatedAccounts,
              });
            }),
          );

          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      }),
    createVendorAccount:
      build.mutation<api.RedactedVendorAccountResponse, api.VendorAccountRequest & { vendorId: api.UUID }>({
        async queryFn({ vendorId, ...request }, { dispatch }) {
          const externalVendorApi = useVendorApi(dispatch);
          const data = await externalVendorApi.createVendorAccount(vendorId, request);
          return { data };
        },
        invalidatesTags: ['Vendor'],
      }),
    createVendorAccountVerificationProviderLink: build.mutation<api.VendorAccountVerificationProviderLinkResponse, api.VendorAccountVerificationProviderLinkRequest>({
      async queryFn(request, { dispatch }) {
        const externalPaymentApi = useVendorApi(dispatch);
        const data = await externalPaymentApi.createVendorAccountVerificationProviderLink(request);
        return { data };
      },
    }),
    createBulkVendorAccounts: build.mutation<api.BulkVendorAccountResponse, api.BulkVendorAccountRequest & { vendorId: api.UUID }>({
      async queryFn({ vendorId, ...request }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.createBulkVendorAccounts(vendorId, request);
        return { data };
      },
      invalidatesTags: ['Vendor'],
    }),
    // Must be a mutation because the underlying endpoint uses POST
    getVendorAccountVerificationProviderLinkResults: build.mutation<api.VendorAccountVerificationProviderLinkResultsResponse, api.VendorAccountVerificationProviderLinkResultsRequest>({
      async queryFn(request, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.getVendorAccountVerificationProviderLinkResults(request);
        return { data };
      },
    }),
    renderLatestServiceAgreement:
      build.query<api.RenderedServiceAgreementResponse | undefined, { type: api.ServiceAgreementType } & api.RenderServiceAgreementRequest>({
        async queryFn({ type, ...params }, { dispatch }) {
          const externalVendorApi = useVendorApi(dispatch);
          const data = await externalVendorApi.renderLatestServiceAgreement(type, params);
          return { data };
        },
      }),
    renderLatestPartnerServiceAgreement:
      build.query<api.RenderedPartnerServiceAgreementResponse | undefined, api.RenderPartnerServiceAgreementRequest & { partnerId: api.Partners }>({
        async queryFn(params: api.RenderPartnerServiceAgreementRequest & { partnerId: api.Partners }, { dispatch }) {
          const externalVendorApi = useVendorApi(dispatch);
          const data = await externalVendorApi.renderLatestPartnerServiceAgreement(params.partnerId, params);
          return { data };
        },
      }),
    getVendorServiceAgreements: build.query<api.IPagedResults<api.RenderedVendorServiceAgreementResponse> | undefined, api.VendorServiceAgreementFilterParams & { vendorId: api.UUID }>({
      async queryFn({ vendorId, ...params }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.getVendorServiceAgreements(vendorId, params);
        return { data };
      },
      providesTags: ['VendorServiceAgreements'],
    }),
    getVendorPartnerServiceAgreements: build.query<api.IPagedResults<api.RenderedVendorPartnerServiceAgreementResponse> | undefined, api.VendorServiceAgreementFilterParams & { vendorId: api.UUID, partnerId: api.Partners }>({
      async queryFn({ partnerId, vendorId, ...params }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.getVendorPartnerServiceAgreements(partnerId, vendorId, params);
        return { data };
      },
      providesTags: ['PartnerVendorServiceAgreements'],
    }),
    signVendorServiceAgreement: build.mutation<api.RenderedVendorServiceAgreementResponse | undefined, { vendorId: api.UUID, serviceAgreementId: api.UUID, signedByName: string }>({
      async queryFn({ vendorId, serviceAgreementId, signedByName }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.signVendorServiceAgreement(vendorId, serviceAgreementId, {
          signedByName,
        });
        return { data };
      },
      invalidatesTags: ['Vendor', 'VendorServiceAgreements'],
    }),
    signVendorPartnerServiceAgreement: build.mutation<api.RenderedVendorPartnerServiceAgreementResponse | undefined, { vendorId: api.UUID, partnerId: api.Partners, serviceAgreementId: api.UUID, signedByName: string }>({
      async queryFn({
        vendorId,
        partnerId,
        serviceAgreementId,
        signedByName,
      }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.signVendorPartnerServiceAgreement(partnerId, vendorId, serviceAgreementId, {
          signedByName,
        });
        return { data };
      },
      invalidatesTags: ['Vendor', 'PartnerVendorServiceAgreements'],
    }),
    getAllUserAccounts: build.query<readonly api.RedactedUserAccountResponse[], { vendorId: api.UUID }>({
      async queryFn({ vendorId }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.getAllUserAccounts<api.RedactedUserAccountResponse>(vendorId);
        return { data };
      },
      providesTags: ['UserAccounts'],
    }),
    createInitialUserAccount: build.mutation<api.RedactedUserAccountResponse, api.UserAccountRequest>({
      async queryFn({ ...body }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.createInitialUserAccount<api.RedactedUserAccountResponse>(body);
        return { data };
      },
      invalidatesTags: ['UserAccounts'],
    }),
    addUserAccountOnboardingData: build.mutation<api.RedactedUserAccountResponse, api.AddUserAccountOnboardingDataRequest & { email: string }>({
      async queryFn({ email, onboardingData }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.addUserAccountOnboardingData<api.RedactedUserAccountResponse>(email, { onboardingData });
        return { data };
      },
    }),
    createChildUserAccount: build.mutation<api.RedactedUserAccountResponse, api.UserAccountRequest & { vendorId: api.UUID }>({
      async queryFn({ vendorId, ...body }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.createChildUserAccount<api.RedactedUserAccountResponse>(vendorId, body);
        return { data };
      },
      invalidatesTags: ['UserAccounts'],
    }),
    getUserAccount: build.query<api.RedactedUserAccountResponse | undefined, { email: string }>({
      async queryFn({ email }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.getUserAccount<api.RedactedUserAccountResponse>(email);
        return { data };
      },
      providesTags: (result, error, params) => [{ type: 'UserAccounts', ...params, ...result }],
    }),
    registerUserAccount: build.mutation<api.RedactedUserAccountResponse | undefined, { email: string }>({
      async queryFn({ email }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.registerUserAccount<api.RedactedUserAccountResponse>(email);
        return { data };
      },
      invalidatesTags: ['UserAccounts'],
    }),
    getVendorPreference: build.query<api.UserAccountPreferencesResponse | undefined, { vendorId: string }>({
      async queryFn({ vendorId }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.getVendorPreferences<api.UserAccountPreferencesResponse>(vendorId);
        return { data };
      },
      providesTags: ['VendorPreference'],
    }),
    updateVendorPreferences: build.mutation<api.UserAccountPreferencesResponse, {
      vendorId: string,
      preferences: api.UpdateVendorPreferenceRequest['preferences'],
    }>({
      async queryFn({ vendorId, preferences }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.updateVendorPreferences<api.UserAccountPreferencesResponse>(vendorId, { preferences });
        return { data };
      },
      invalidatesTags: ['VendorPreference'],
    }),
    getUnsubscribeStatus: build.query<api.UnsubscribeStatusResponse | undefined, { token: string }>({
      async queryFn({ token }, { dispatch }) {
        const emailUnsubscribeApi = useEmailUnsubscribeApi(dispatch);
        const data = await emailUnsubscribeApi.getUnsubscribeStatus<api.UnsubscribeStatusResponse>(token);
        return { data };
      },
    }),
    updateUnsubscribePreferences: build.mutation<api.UnsubscribeStatusResponse | undefined, { token: string; sendPromotionalEmails: boolean }>({
      async queryFn({ token, sendPromotionalEmails }, { dispatch }) {
        const emailUnsubscribeApi = useEmailUnsubscribeApi(dispatch);
        const data = await emailUnsubscribeApi.updateUnsubscribePreferences<api.UnsubscribeStatusResponse>(token, { sendPromotionalEmails });
        return { data };
      },
      async onQueryStarted({ token }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;

        dispatch(vendorApi.util.updateQueryData('getUnsubscribeStatus', { token }, () => data));
      },
    }),
    getAllVendorDocuments: build.query<api.IPagedResults<api.MaybeRedactedVendorDocumentResponse>, api.VendorDocumentFilterParams & { vendorId: string }>({
      async queryFn({ vendorId, ...params }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.getVendorDocuments(vendorId, params);
        return { data };
      },
      providesTags: ['VendorDocuments'],
    }),
    getPrograms: build.query<readonly api.MaybeRedactedProgramResponse[], api.GetProgramsFilterParams>({
      async queryFn(params, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.getPrograms(params);
        return { data };
      },
      providesTags: ['Programs'],
    }),
    uploadVendorDocument: build.mutation<api.VendorDocumentResponse, {
      vendorId: string,
      vendorAccountId?: string,
      documentType: api.VendorDocumentType,
      file: File,
    }>({
      query({ vendorId, vendorAccountId, documentType, file }) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('documentType', documentType);

        if (vendorAccountId) {
          formData.append('vendorAccountId', vendorAccountId);
        }

        return {
          url: `vendors/${vendorId}/documents`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Vendor', 'VendorDocuments'],
    }),
    bulkUploadVendorDocuments: build.mutation<api.BulkUploadVendorDocumentsResponse, {
      vendorId: string,
      documentType: api.VendorDocumentType,
      files: File[],
    }>({
      query({ vendorId, documentType, files }) {
        const formData = new FormData();
        formData.append('documentType', documentType);
        files.forEach((file) => formData.append('files', file, file.name));

        return {
          url: `vendors/${vendorId}/documents/bulk`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Vendor', 'VendorDocuments'],
    }),
    getVendorDocumentOcr: build.query<api.VendorBusinessVerificationDocumentContents, api.GetVendorDocumentOcrRequest & { vendorId: string, documentId: string }>({
      async queryFn({ vendorId, documentId }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.getVendorDocumentOcr(vendorId, documentId, {});
        // Transform zip to postalCode
        if (data) {
          const { zip, ...rest } = data as any;
          return {
            data: zip ? { ...rest, postalCode: zip } : rest
          };
        }
        return { data };
      },
    }),
    updateVendorDocumentContents: build.mutation<api.MaybeRedactedVendorDocumentResponse | undefined, Omit<api.UpdateVendorDocumentContentsRequest, 'contents'> & {
      vendorId: string,
      documentId: string,
      contents?: Omit<NonNullable<api.UpdateVendorDocumentContentsRequest['contents']>, 'zip'> & { postalCode?: string };
    }>({
      async queryFn({ vendorId, documentId, contents, ...params }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        // Transform postalCode to zip for external API
        const transformedContents = contents ? (() => {
          const { postalCode, ...rest } = contents as any;
          return postalCode ? { ...rest, zip: postalCode } : rest;
        })() : undefined;
        const data = await externalVendorApi.updateVendorDocumentContents<api.MaybeRedactedVendorDocumentResponse>(
          vendorId,
          documentId,
          { ...params, ...(transformedContents && { contents: transformedContents }) } as any
        );
        return { data };
      },
      invalidatesTags: ['VendorDocuments'],
    }),
    rejectVendorDocument: build.mutation<api.MaybeRedactedVendorDocumentResponse | undefined, { vendorId: string, documentId: string, rejectedReason?: api.VendorDocumentRejectedReason }>({
      async queryFn({ vendorId, documentId, rejectedReason }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.rejectVendorDocument<api.MaybeRedactedVendorDocumentResponse>(vendorId, documentId, { rejectedReason });
        return { data };
      },
      invalidatesTags: ['VendorDocuments'],
    }),
    createVendorFeedback: build.mutation<api.VendorFeedbackResponse | undefined, api.CreateVendorFeedbackRequest>({
      async queryFn(params, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.createVendorFeedback(params);

        return { data };
      },
    }),
    patchUserAccount: build.mutation<api.RedactedUserAccountResponse | undefined, api.PatchUserAccountRequest & { userAccountId: string }>({
      async queryFn({ userAccountId, ...params }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.patchUserAccount(userAccountId, params);
        return { data };
      },
    }),
    patchVendorFeatures: build.mutation<api.VendorAggregateResponse | undefined, api.PatchVendorFeaturesRequest & { vendorId: api.UUID }>({
      async queryFn({ vendorId, ...params }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.patchVendorFeatures(vendorId, params);
        return { data };
      },
      invalidatesTags: ['Vendor'],
    }),
    logUserAccountActivity: build.mutation<void, api.LogUserAccountActivityRequest & { email: string }>({
      async queryFn({ email, activity, resource }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.logUserAccountActivity(email, { activity, resource });
        return { data };
      },
    }),
    updateVendorAccountUnderwriting: build.mutation<void, { vendorId: string, vendorAccountId: string }>({
      async queryFn({ vendorId, vendorAccountId }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.updateVendorAccountUnderwriting(vendorId, vendorAccountId, {});
        return { data };
      },
      invalidatesTags: ['Vendor'],
    }),
    updateInstantPayoutAlwaysOn: build.mutation<api.VendorAggregateResponse | undefined, api.UpdateInstantPayoutAlwaysOnRequest & { vendorId: api.UUID }>({
      async queryFn({ vendorId, ...params }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.updateInstantPayoutAlwaysOn(vendorId, params);
        return { data };
      },
      invalidatesTags: ['Vendor'],
    }),
    acceptPricing: build.mutation<api.RedactedVendorResponse | undefined, { vendorId: string, hasAcceptedInitialProgram: boolean, partnerId: Partners, programCode: ProgramCodes }>({
      async queryFn({ vendorId, hasAcceptedInitialProgram, partnerId, programCode }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.acceptPricing(vendorId, {
          hasAcceptedInitialProgram,
          partnerId,
          programCode,
        });
        return { data };
      },
      invalidatesTags: ['Vendor'],
    }),
    acknowledgeDataSharingAgreement: build.mutation<api.MaybeRedactedVendorProgramResponse | undefined, { vendorId: string; productCode: api.ProductCodes; partnerId: Partners }>({
      async queryFn({ vendorId, productCode, partnerId }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.acknowledgeDataSharingAgreement(vendorId, productCode, partnerId);
        return { data };
      },
      invalidatesTags: ['Vendor'],
    }),
    acknowledgeDirectDebitAgreement: build.mutation<api.RedactedVendorResponse | undefined, { vendorId: string, vendorAccountId: UUID }>({
      async queryFn({ vendorId, vendorAccountId }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.acknowledgeDirectDebitAgreement(vendorId, {
          vendorAccountId,
        });
        return { data };
      },
      invalidatesTags: ['Vendor'],
    }),
    createVendorAccountTarget: build.mutation<api.RedactedVendorAccountResponse | undefined, { vendorId: string, vendorAccountId: UUID, vendorAccountTarget: VendorAccountTargetRequest }>({
      async queryFn({ vendorId, vendorAccountId, vendorAccountTarget }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.createVendorAccountTarget(vendorId, vendorAccountId, vendorAccountTarget);

        return { data };
      },
      invalidatesTags: ['Vendor'],
    }),
    patchVendorAccountTarget: build.mutation<api.RedactedVendorAccountResponse | undefined, { vendorId: string, vendorAccountId: UUID, vendorAccountTarget: PatchVendorAccountTargetRequest }>({
      async queryFn({ vendorId, vendorAccountId, vendorAccountTarget }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.patchVendorAccountTarget(vendorId, vendorAccountId, {
          partnerId: vendorAccountTarget.partnerId,
          direction: vendorAccountTarget.direction,
        });
        return { data };
      },
      invalidatesTags: ['Vendor'],
    }),
    getVendorProgramPromotions: build.query<readonly api.RedactedVendorProgramPromotionResponse[], void>({
      async queryFn(_, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.getVendorProgramPromotions();
        return { data };
      },
      providesTags: ['ProgramPromotions'],
    }),
    dismissProgramPromotion: build.mutation<api.RedactedVendorProgramPromotionResponse | undefined, api.UUID>({
      async queryFn(id, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.dismissVendorProgramPromotion(id);
        return { data };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          vendorApi.util.updateQueryData('getVendorProgramPromotions', undefined, (draft) =>
            draft.filter((p) => p.id !== id) as readonly api.RedactedVendorProgramPromotionResponse[],
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ['ProgramPromotions'],
    }),
    clickProgramPromotion: build.mutation<api.RedactedVendorProgramPromotionResponse | undefined, api.UUID>({
      async queryFn(id, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.clickVendorProgramPromotion(id);
        return { data };
      },
    }),
    completeProgramPromotion: build.mutation<api.RedactedVendorProgramPromotionResponse | undefined, { id: api.UUID, details?: api.VendorProgramPromotionDetails }>({
      async queryFn({ id, details }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.completeVendorProgramPromotion(id, details ? { details } : undefined);
        return { data };
      },
      invalidatesTags: ['Programs', 'Vendor', 'ProgramPromotions'],
    }),
    getFactoringEligibility: build.query<api.RedactedVendorProgramPromotionResponse | undefined, void>({
      async queryFn(_, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        // A 204 (no promotion) can resolve to an empty string rather than undefined
        // depending on the response body, so normalize any falsy result explicitly.
        const data = (await externalVendorApi.getFactoringEligibility()) || undefined;
        return { data };
      },
      providesTags: ['ProgramPromotions'],
    }),
    getVendorProgramPortalConfiguration: build.query<api.RedactedVendorProgramPortalConfigurationResponse | undefined, { vendorId: api.UUID, productCode: api.ProductCodes, partnerId: Partners }>({
      async queryFn({ vendorId, productCode, partnerId }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        // Same 204-normalization as getFactoringEligibility above.
        const data = (await externalVendorApi.getVendorProgramPortalConfiguration(vendorId, productCode, partnerId)) || undefined;
        return { data };
      },
      providesTags: ['VendorProgramPortalConfiguration'],
    }),
    grantVendorProgramPortalAccess: build.mutation<api.VendorProgramPortalConfigurationResponse | undefined, { vendorId: api.UUID, productCode: api.ProductCodes, partnerId: Partners }>({
      async queryFn({ vendorId, productCode, partnerId }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.grantVendorProgramPortalAccess(vendorId, productCode, partnerId);
        return { data };
      },
      invalidatesTags: ['VendorProgramPortalConfiguration'],
    }),
    getFaqs: build.query<readonly api.FaqSectionResponse[], api.FaqScope & { vendorId?: api.UUID }>({
      async queryFn(scope, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);
        const data = await externalVendorApi.getFaqs(scope);
        return { data };
      },
    }),
    selfProvisionVendorProgram: build.mutation<api.MaybeRedactedVendorProgramResponse | undefined, { vendorId: api.UUID, productCode: api.ProductCodes, partnerId: Partners, request?: api.SelfProvisionVendorProgramRequest }>({
      async queryFn({ vendorId, productCode, partnerId, request }, { dispatch }) {
        const externalVendorApi = useVendorApi(dispatch);

        try {
          const data = await externalVendorApi.selfProvisionVendorProgram(vendorId, productCode, partnerId, request);
          return { data };
        } catch (error) {
          // returned rather than thrown so callers can tell a 409 (already provisioned) from a 400, and a 409 still refetches the vendor
          const message = error instanceof Error ? error.message : 'Unknown error';
          if (error instanceof api.ApiError && error.statusCode !== undefined) {
            return { error: { status: error.statusCode, data: message } };
          }
          return { error: { status: 'CUSTOM_ERROR', error: message } };
        }
      },
      invalidatesTags: (_result, error) => (error && error.status !== 409 ? [] : ['Vendor', 'VendorProgramPortalConfiguration']),
    }),
  }),
});

export const {
  useAcknowledgeDataSharingAgreementMutation,
  useAddUserAccountOnboardingDataMutation,
  useConfirmVerificationMutation,
  useCreateAssignedVendorMutation,
  useCreateChildUserAccountMutation,
  useCreateVendorAccountMutation,
  useCreateVendorAccountVerificationProviderLinkMutation,
  useCreateUnassignedVendorMutation,
  useCreateVendorFeedbackMutation,
  useGetAllUserAccountsQuery,
  useGetAllVendorDocumentsQuery,
  useGetProgramsQuery,
  useGetUserAccountQuery,
  useGetVendorDocumentOcrQuery,
  useGetVendorPartnerServiceAgreementsQuery,
  useGetVendorPreferenceQuery,
  useGetVendorQuery,
  useGetVendorServiceAgreementsQuery,
  useLazyGetVendorDocumentOcrQuery,
  useLazyValidateOnboardingIdQuery,
  usePatchUserAccountMutation,
  usePatchVendorFeaturesMutation,
  useRejectVendorDocumentMutation,
  useRenderLatestPartnerServiceAgreementQuery,
  useRenderLatestServiceAgreementQuery,
  useRequestInflightsMutation,
  useSignVendorPartnerServiceAgreementMutation,
  useSignVendorServiceAgreementMutation,
  useUpdateVendorMutation,
  useUpdateVendorDocumentContentsMutation,
  useUpdateVendorPreferencesMutation,
  useGetUnsubscribeStatusQuery,
  useUpdateUnsubscribePreferencesMutation,
  useUploadVendorDocumentMutation,
  useBulkUploadVendorDocumentsMutation,
  useLogUserAccountActivityMutation,
  useUpdateVendorAccountUnderwritingMutation,
  useUpdateInstantPayoutAlwaysOnMutation,
  useAcceptPricingMutation,
  useAcknowledgeDirectDebitAgreementMutation,
  useCreateBulkVendorAccountsMutation,
  useGetVendorAccountVerificationProviderLinkResultsMutation,
  useCreateVendorAccountTargetMutation,
  usePatchVendorAccountTargetMutation,
  useGetVendorProgramPromotionsQuery,
  useDismissProgramPromotionMutation,
  useClickProgramPromotionMutation,
  useCompleteProgramPromotionMutation,
  useGetFactoringEligibilityQuery,
  useGetFaqsQuery,
  useGetVendorProgramPortalConfigurationQuery,
  useGrantVendorProgramPortalAccessMutation,
  useSelfProvisionVendorProgramMutation,
  util,
} = vendorApi;